import type { SessionKind } from "../hooks/audit/useControlSessionEnvelope";

/**
 * Base paths for the session-scoped routes that exist for both entities, for
 * whichever kind of session the caller holds.
 *
 * A work step session answers every one of these under a single prefix
 * (`/work-steps/work-step-session/{id}`), because one router owns them. The
 * control side grew up split across two routers, so which control prefix a
 * route lives under is not derivable — it has to be stated per family. Hence
 * two helpers rather than one.
 *
 * These are helpers rather than inline ternaries for the reason
 * `workPaperTemplateApi.ts` records: a control-only path branched at some call
 * sites and missed at others fails invisibly, because the control route answers
 * 200 for a control session and 404s for a work-step one, so nothing breaks
 * until someone opens the page on a work step.
 */

/**
 * The frontend route prefix a session's pages (Support, Documents, Workpaper)
 * live under, `/audit/{control,work-step}-sessions/session`, for whichever kind
 * of session the caller holds.
 */
export const sessionRouteBase = (sessionKind: SessionKind): string =>
  sessionKind === "work_step"
    ? "/audit/work-step-sessions/session"
    : "/audit/control-sessions/session";

/**
 * Routes the controls v2 router serves for a control session: evidence upload
 * (`/evidence/parse|confirm|preview/...`), `/download-documents`,
 * `/documents/search` and `/audit-log`.
 */
export const controlsV2SessionApiBase = (
  sessionKind: SessionKind,
  sessionId: string,
): string =>
  sessionKind === "work_step"
    ? `/work-steps/work-step-session/${sessionId}`
    : `/controls/v2/control-session/${sessionId}`;

/**
 * Routes the matching router serves for a control session: the session's
 * `/documentation` listing and the three supplementary-document routes
 * (`/upload-supplementary-documents`, `/supplementary-document/{id}` and its
 * `/download`).
 */
export const matchingSessionApiBase = (
  sessionKind: SessionKind,
  sessionId: string,
): string =>
  sessionKind === "work_step"
    ? `/work-steps/work-step-session/${sessionId}`
    : `/matching/control-session/${sessionId}`;
